/**
 * Écran « Justificatifs » : état des pièces des opérations bancaires de
 * l'exercice, suivi de semaine en semaine, mois rapprochés, lignes en attente
 * et règles de dispense. La lecture consulte ; l'équipe gère les règles.
 * Rien n'est écrit dans Zoho.
 */

import { dateFr, entier, eurosCentimes, horodatageFr, lundiDe } from '@scc/core';
import { ErreurApi, type Identite } from '../../api.js';
import { enteteSection, kpi, noteLecture } from '../../composants.js';
import { h, vider } from '../../dom.js';
import {
  creerRegleDispense,
  etatJustificatifs,
  lignesJustificatifs,
  modifierRegleDispense,
  reglesDispense,
  testerRegleDispense,
  type ChampRegle,
  type CritereRegle,
  type EtatJustificatif,
  type EtatJustificatifs,
  type LigneJustificatif,
  type OperateurRegle,
  type RegleDispense,
  type SensRegle,
  type StatutRegle,
} from './api.js';
import { table, verrouille } from './communs.js';
import { ongletPiecesRecues } from './pieces.js';

const ETATS: Readonly<Record<EtatJustificatif, string>> = {
  a_justifier: 'À justifier',
  a_categoriser: 'À catégoriser',
  non_evalue: 'Pièce non évaluée',
  dispense: 'Dispensée',
  facture: 'Facture émise',
  justifie: 'Pièce jointe',
};

const CHAMPS: Readonly<Record<ChampRegle, string>> = {
  tiers: 'Tiers',
  libelle: 'Libellé bancaire',
  compte: 'Code du compte',
  compte_libelle: 'Compte',
  type_transaction: 'Type d’opération Zoho',
};

const OPERATEURS: Readonly<Record<OperateurRegle, string>> = {
  contient: 'contient',
  egal: 'est égal à',
  commence_par: 'commence par',
};

const SENS: Readonly<Record<SensRegle, string>> = { depense: 'Dépenses', recette: 'Recettes', tous: 'Les deux sens' };

const STATUTS: Readonly<Record<StatutRegle, string>> = { proposee: 'Proposée', active: 'Active', inactive: 'Inactive' };

const moisFr = (m: string): string =>
  new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${m}-01T00:00:00Z`));

const pct = (x: number): string => `${String(Math.round(x * 100))} %`;

const critere = (r: CritereRegle): string =>
  `${CHAMPS[r.champ]} ${OPERATEURS[r.operateur]} « ${r.valeur} » — ${SENS[r.sens].toLowerCase()}` +
  (r.montantMax === null ? '' : `, jusqu’à ${eurosCentimes(r.montantMax)}`);

export function vueJustificatifs(identite: Identite): HTMLElement {
  const admin = identite.role === 'administration';
  const retour = h('div', { role: 'status' });
  const signaler = (texte: string, ok: boolean): void => {
    vider(retour);
    retour.appendChild(h('p', { classe: `retour-editeur ${ok ? 'ok' : 'ko'}`, role: ok ? null : 'alert' }, texte));
  };
  const echec = (e: unknown): void => {
    signaler(e instanceof ErreurApi ? e.message : 'Opération refusée.', false);
  };

  const choixExercice = h('select', { id: 'justificatifs-exercice', 'aria-label': 'Exercice' });
  const zoneChiffres = h('div', {}, noteLecture('Chargement…'));
  const zoneSuivi = h('div', {});
  const zoneMois = h('div', {});
  const zoneCartes = h('div', {});
  const zoneLignes = h('div', {});
  const zoneRegles = h('div', {});

  const filtreEtat = h(
    'select',
    { id: 'justificatifs-etat' },
    h('option', { value: '' }, 'Tous les états'),
    ...(Object.keys(ETATS) as EtatJustificatif[]).map((e) => h('option', { value: e }, ETATS[e])),
  );
  filtreEtat.value = 'a_justifier';
  const filtreSens = h(
    'select',
    { id: 'justificatifs-sens' },
    h('option', { value: '' }, 'Recettes et dépenses'),
    h('option', { value: 'depense' }, 'Dépenses'),
    h('option', { value: 'recette' }, 'Recettes'),
  );
  const filtreMois = h('select', { id: 'justificatifs-mois' }, h('option', { value: '' }, 'Tous les mois'));
  const filtreCarte = h('select', { id: 'justificatifs-carte' }, h('option', { value: '' }, 'Toutes les opérations'));

  let exercice: number | undefined;
  /** Exercice servi par défaut au premier chargement. */
  let exerciceCourant: number | undefined;
  let generationLignes = 0;

  // ── Chiffres clés, semaines, mois ─────────────────────────────────────
  const dessinerEtat = (d: EtatJustificatifs): void => {
    const s = d.synthese;
    const p = s.parEtat;
    vider(zoneChiffres);
    zoneChiffres.appendChild(
      h(
        'div',
        { classe: 'kpi-grid' },
        kpi(p.a_justifier.nb > 0 ? 'warning' : 'progress', entier(p.a_justifier.nb), null, `À justifier · ${eurosCentimes(p.a_justifier.montant)}`),
        kpi(p.a_categoriser.nb > 0 ? 'warning' : 'progress', entier(p.a_categoriser.nb), null, `À catégoriser dans Zoho · ${eurosCentimes(p.a_categoriser.montant)}`),
        kpi('neutral', entier(p.dispense.nb), null, 'Dispensées par une règle validée'),
        kpi('progress', s.tauxJustifie === null ? '—' : pct(s.tauxJustifie), null, 'Lignes qui ne demandent plus rien'),
        kpi(
          s.moisNonRapproches > 0 ? 'alert' : 'progress',
          s.dernierMoisRapproche === null ? 'aucun' : moisFr(s.dernierMoisRapproche),
          null,
          `Dernier mois rapproché · ${entier(s.moisNonRapproches)} mois à rapprocher`,
        ),
      ),
    );
    const k = d.collecte;
    zoneChiffres.appendChild(
      h(
        'div',
        { classe: 'kpi-grid', role: 'group', 'aria-label': 'Factures reçues par courriel' },
        kpi(k.aValider > 0 ? 'warning' : 'progress', entier(k.aValider), null, 'Pièces reçues à valider'),
        kpi(k.aArbitrer > 0 ? 'warning' : 'progress', entier(k.aArbitrer), null, 'Pièces reçues à arbitrer'),
        kpi(k.aTelecharger > 0 ? 'warning' : 'progress', entier(k.aTelecharger), null, 'Factures à télécharger'),
        kpi('neutral', entier(k.deposees30j), null, 'Déposées dans le Drive depuis 30 jours'),
      ),
    );
    if (p.non_evalue.nb > 0) {
      // Le détail des pièces n'est relu que pour l'exercice courant : sur un exercice passé, il ne reviendra pas.
      zoneChiffres.appendChild(
        noteLecture(
          d.exercice === exerciceCourant
            ? `**${entier(p.non_evalue.nb)} ligne(s)** dont la pièce n’a pas pu être lue dans Zoho (instantané livré ou lecture en échec) : ` +
                'elles seront évaluées à la prochaine ingestion.'
            : `**${entier(p.non_evalue.nb)} ligne(s)** dont la pièce n’est pas connue : le détail des pièces n’est lu que pour l’exercice en cours.`,
        ),
      );
    }
    zoneChiffres.appendChild(
      noteLecture(
        d.derniereIngestion
          ? `Dernière ingestion le ${horodatageFr(d.derniereIngestion)}. Une pièce jointe dans Zoho l’emporte toujours sur une règle de dispense.`
          : 'Aucune ingestion enregistrée.',
      ),
    );

    vider(zoneSuivi);
    zoneSuivi.appendChild(
      d.suivi.length === 0
        ? noteLecture('Le suivi commence avec la prochaine ingestion de Zoho : un point est enregistré chaque jour, le dernier de chaque semaine est affiché.')
        : table(
            'Suivi de semaine en semaine',
            ['Semaine du', ['À justifier', 'num'], ['Montant', 'num'], ['À catégoriser', 'num'], ['Dispensées', 'num'], ['Mois à rapprocher', 'num']],
            d.suivi.map((x) =>
              h(
                'tr',
                {},
                h('td', {}, dateFr(lundiDe(x.date))),
                h('td', { classe: 'num' }, entier(x.aJustifier.nb)),
                h('td', { classe: 'num' }, eurosCentimes(x.aJustifier.montant)),
                h('td', { classe: 'num' }, entier(x.aCategoriser.nb)),
                h('td', { classe: 'num' }, entier(x.dispense.nb)),
                h('td', { classe: 'num' }, entier(x.moisNonRapproches)),
              ),
            ),
          ),
    );

    vider(zoneMois);
    zoneMois.appendChild(
      s.mois.length === 0
        ? noteLecture('Aucune opération sur cet exercice.')
        : table(
            'Justificatifs et rapprochement, mois par mois',
            ['Mois', ['Lignes', 'num'], ['À justifier', 'num'], ['À catégoriser', 'num'], ['Dispensées', 'num'], 'Rapprochement'],
            s.mois.map((m) =>
              h(
                'tr',
                {},
                h(
                  'td',
                  {},
                  h(
                    'button',
                    {
                      type: 'button',
                      classe: 'lien-texte',
                      title: 'Voir les lignes du mois',
                      surClick: () => {
                        filtreMois.value = m.mois;
                        filtreEtat.value = '';
                        void chargerLignes();
                        zoneLignes.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      },
                    },
                    moisFr(m.mois),
                  ),
                ),
                h('td', { classe: 'num' }, entier(m.lignes)),
                h('td', { classe: 'num' }, entier(m.parEtat.a_justifier.nb)),
                h('td', { classe: 'num' }, entier(m.parEtat.a_categoriser.nb)),
                h('td', { classe: 'num' }, entier(m.parEtat.dispense.nb)),
                h('td', {}, m.rapproche ? 'Rapproché' : `${entier(m.rapprochees)} / ${entier(m.lignes)} lignes`),
              ),
            ),
          ),
    );

    const moisCourant = filtreMois.value;
    vider(filtreMois);
    filtreMois.appendChild(h('option', { value: '' }, 'Tous les mois'));
    for (const m of s.mois) filtreMois.appendChild(h('option', { value: m.mois }, moisFr(m.mois)));
    filtreMois.value = s.mois.some((m) => m.mois === moisCourant) ? moisCourant : '';

    dessinerCartes(d.cartes);
  };

  // ── À qui demander les pièces ─────────────────────────────────────────
  const nomCarte = (carte: string, nom: string | null): string => `carte ${carte} · ${nom ?? 'titulaire à renseigner'}`;

  const voirLignesCarte = (carte: string, etat: EtatJustificatif): void => {
    filtreCarte.value = carte;
    filtreEtat.value = etat;
    filtreMois.value = '';
    void chargerLignes();
    zoneLignes.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const compteCarte = (carte: string, etat: EtatJustificatif, c: { nb: number; montant: number }): HTMLElement | string =>
    c.nb === 0
      ? '—'
      : h(
          'button',
          {
            type: 'button',
            classe: 'lien-texte',
            title: `Voir ces lignes (${ETATS[etat].toLowerCase()})`,
            surClick: () => {
              voirLignesCarte(carte, etat);
            },
          },
          `${entier(c.nb)} · ${eurosCentimes(c.montant)}`,
        );

  const dessinerCartes = (cartes: EtatJustificatifs['cartes']): void => {
    vider(zoneCartes);
    zoneCartes.appendChild(
      cartes.length === 0
        ? noteLecture('Aucun paiement par carte n’attend de pièce sur cet exercice.')
        : table(
            'Paiements par carte qui attendent une pièce',
            ['Titulaire de la carte', ['À justifier', 'num'], ['À catégoriser dans Zoho', 'num']],
            cartes.map((c) =>
              h(
                'tr',
                {},
                h(
                  'td',
                  {},
                  c.nom === null
                    ? h('span', {}, `Carte ${c.carte} · `, h('em', {}, 'titulaire à renseigner'))
                    : h('span', {}, h('strong', {}, c.nom), ` · carte ${c.carte}`),
                ),
                h('td', { classe: 'num' }, compteCarte(c.carte, 'a_justifier', c.aJustifier)),
                h('td', { classe: 'num' }, compteCarte(c.carte, 'a_categoriser', c.aCategoriser)),
              ),
            ),
          ),
    );
    if (cartes.some((c) => c.nom === null)) {
      zoneCartes.appendChild(
        noteLecture('Le titulaire d’une carte se renseigne dans **Modules › Justificatifs**, réglage « Titulaires des cartes bancaires ».'),
      );
    }

    const carteCourante = filtreCarte.value;
    vider(filtreCarte);
    filtreCarte.appendChild(h('option', { value: '' }, 'Toutes les opérations'));
    for (const c of cartes) filtreCarte.appendChild(h('option', { value: c.carte }, c.nom === null ? `Carte ${c.carte}` : `${c.nom} (carte ${c.carte})`));
    // Une carte choisie qui n'a plus rien en attente reste choisie : ses autres lignes restent visibles.
    if (carteCourante && carteCourante !== 'sans' && !cartes.some((c) => c.carte === carteCourante)) {
      filtreCarte.appendChild(h('option', { value: carteCourante }, `Carte ${carteCourante}`));
    }
    filtreCarte.appendChild(h('option', { value: 'sans' }, 'Sans carte'));
    filtreCarte.value = carteCourante;
  };

  // ── Lignes en attente ─────────────────────────────────────────────────
  const ligne = (l: LigneJustificatif): HTMLElement =>
    h(
      'tr',
      {},
      h('td', { classe: 'date' }, dateFr(l.date)),
      h(
        'td',
        { classe: 'operation' },
        h('strong', {}, l.tiers || l.libelle || '—'),
        l.tiers && l.libelle ? h('div', { classe: 'note-lecture' }, l.libelle) : null,
        l.compteLibelle ? h('div', { classe: 'note-lecture' }, l.compteLibelle) : null,
        l.carte === null ? null : h('div', { classe: 'note-lecture' }, `Payé avec la ${nomCarte(l.carte, l.titulaire)}`),
      ),
      h('td', { classe: 'num' }, `${l.sens === 'recette' ? '+' : '−'} ${eurosCentimes(l.montant)}`),
      h('td', {}, ETATS[l.etat], l.motif ? h('div', { classe: 'note-lecture' }, l.motif) : null),
      h(
        'td',
        { classe: 'zoho' },
        l.lienZoho
          ? h('a', { href: l.lienZoho, target: '_blank', rel: 'noopener noreferrer', 'aria-label': 'Ouvrir la dépense dans Zoho Books' }, 'Ouvrir ↗')
          : '—',
      ),
    );

  const chargerLignes = async (): Promise<void> => {
    if (exercice === undefined) return;
    const moi = ++generationLignes;
    try {
      const { lignes } = await lignesJustificatifs({
        exercice,
        ...(filtreEtat.value ? { etat: filtreEtat.value as EtatJustificatif } : {}),
        ...(filtreSens.value ? { sens: filtreSens.value as 'recette' | 'depense' } : {}),
        ...(filtreMois.value ? { mois: filtreMois.value } : {}),
        ...(filtreCarte.value ? { carte: filtreCarte.value } : {}),
      });
      if (moi !== generationLignes) return;
      vider(zoneLignes);
      if (lignes.length === 0) {
        zoneLignes.appendChild(noteLecture('Aucune ligne pour ces filtres.'));
        return;
      }
      const total = lignes.reduce((n, l) => n + l.montant, 0);
      zoneLignes.appendChild(noteLecture(`**${entier(lignes.length)} ligne(s)**, ${eurosCentimes(total)}.`));
      zoneLignes.appendChild(table('Lignes de l’exercice', ['Date', 'Opération', ['Montant', 'num'], 'État', 'Zoho'], lignes.map(ligne)));
    } catch (e) {
      if (moi !== generationLignes) return;
      vider(zoneLignes);
      zoneLignes.appendChild(noteLecture('Les lignes n’ont pas pu être chargées.'));
      echec(e);
    }
  };
  for (const f of [filtreEtat, filtreSens, filtreMois, filtreCarte]) {
    f.addEventListener('change', () => {
      void chargerLignes();
    });
  }

  // ── Règles de dispense ────────────────────────────────────────────────
  const changerStatut = (r: RegleDispense, statut: StatutRegle, bouton: HTMLButtonElement): void => {
    void (async () => {
      try {
        await verrouille([bouton], () => modifierRegleDispense(r.id, { statut }));
        signaler(statut === 'active' ? 'Règle validée : ses lignes sont dispensées.' : 'Règle désactivée.', true);
        await toutCharger();
      } catch (e) {
        echec(e);
      }
    })();
  };

  const actionsRegle = (r: RegleDispense): HTMLElement | string => {
    if (!admin) return '—';
    const bouton = (libelle: string, statut: StatutRegle): HTMLButtonElement => {
      const b: HTMLButtonElement = h(
        'button',
        {
          type: 'button',
          classe: 'btn-action',
          surClick: () => {
            changerStatut(r, statut, b);
          },
        },
        libelle,
      );
      return b;
    };
    return h(
      'div',
      { classe: 'actions-arbitrage' },
      r.statut !== 'active' ? bouton(r.statut === 'proposee' ? 'Valider' : 'Réactiver', 'active') : null,
      r.statut !== 'inactive' ? bouton(r.statut === 'proposee' ? 'Écarter' : 'Désactiver', 'inactive') : null,
    );
  };

  const formulaire = (): HTMLElement => {
    const champ = h('select', { id: 'dispense-champ' }, ...(Object.keys(CHAMPS) as ChampRegle[]).map((c) => h('option', { value: c }, CHAMPS[c])));
    champ.value = 'compte_libelle';
    const operateur = h(
      'select',
      { id: 'dispense-operateur' },
      ...(Object.keys(OPERATEURS) as OperateurRegle[]).map((o) => h('option', { value: o }, OPERATEURS[o])),
    );
    const valeur = h('input', { type: 'text', id: 'dispense-valeur', maxlength: 200 });
    const sens = h('select', { id: 'dispense-sens' }, ...(Object.keys(SENS) as SensRegle[]).map((s) => h('option', { value: s }, SENS[s])));
    sens.value = 'depense';
    const plafond = h('input', { type: 'number', id: 'dispense-plafond', min: 0, step: '0.01', placeholder: 'sans plafond' });
    const motif = h('input', { type: 'text', id: 'dispense-motif', maxlength: 300, placeholder: 'Pourquoi aucune pièce n’est attendue' });
    const zoneTest = h('div', {});

    const lire = (): CritereRegle | null => {
      const v = valeur.value.trim();
      if (v === '') {
        signaler('Indiquez la valeur recherchée.', false);
        return null;
      }
      const p = plafond.value.trim() === '' ? null : Number(plafond.value);
      if (p !== null && (!Number.isFinite(p) || p <= 0)) {
        signaler('Plafond invalide : un montant positif, ou rien.', false);
        return null;
      }
      return { champ: champ.value as ChampRegle, operateur: operateur.value as OperateurRegle, valeur: v, sens: sens.value as SensRegle, montantMax: p };
    };

    const boutonTester = h('button', { type: 'button', classe: 'btn-action' }, 'Voir les lignes captées');
    const boutonEnregistrer = h('button', { type: 'button', classe: 'btn-action' }, 'Enregistrer la règle');
    const controles = [champ, operateur, valeur, sens, plafond, motif, boutonTester, boutonEnregistrer];

    boutonTester.addEventListener('click', () => {
      const c = lire();
      if (!c) return;
      void (async () => {
        try {
          const t = await verrouille(controles, () => testerRegleDispense(c));
          vider(zoneTest);
          zoneTest.appendChild(
            h(
              'div',
              { classe: 'card' },
              h('p', {}, h('strong', {}, `${entier(t.lignes)} ligne(s) sans pièce captée(s) sur l’exercice en cours`), t.lignes > 10 ? ' — les 10 plus récentes :' : '.'),
              t.exemples.length > 0
                ? h('ul', { classe: 'exemples-regle' }, ...t.exemples.map((x) => h('li', {}, `${dateFr(x.date)} — ${x.tiers || x.libelle || '—'} — ${eurosCentimes(x.montant)}`)))
                : null,
            ),
          );
        } catch (e) {
          echec(e);
        }
      })();
    });

    boutonEnregistrer.addEventListener('click', () => {
      const c = lire();
      if (!c) return;
      const m = motif.value.trim();
      if (m === '') {
        signaler('Le motif est obligatoire : il s’affiche sur chaque ligne dispensée.', false);
        return;
      }
      void (async () => {
        try {
          await verrouille(controles, () => creerRegleDispense({ ...c, motif: m }));
          signaler('Règle enregistrée et active.', true);
          valeur.value = '';
          plafond.value = '';
          motif.value = '';
          vider(zoneTest);
          await toutCharger();
        } catch (e) {
          echec(e);
        }
      })();
    });

    return h(
      'div',
      { classe: 'card' },
      h('h3', {}, 'Nouvelle règle de dispense'),
      h(
        'div',
        { classe: 'editeur-champs' },
        h('label', {}, 'Champ', champ),
        h('label', {}, 'Opérateur', operateur),
        h('label', {}, 'Valeur', valeur),
        h('label', {}, 'Sens', sens),
        h('label', {}, 'Plafond (€)', plafond),
        h('label', {}, 'Motif', motif),
      ),
      h('div', { classe: 'barre-actions' }, boutonTester, boutonEnregistrer),
      zoneTest,
    );
  };
  const carteFormulaire = admin ? formulaire() : null;

  const dessinerRegles = (regles: readonly RegleDispense[]): void => {
    vider(zoneRegles);
    const proposees = regles.filter((r) => r.statut === 'proposee').length;
    if (proposees > 0) {
      zoneRegles.appendChild(
        noteLecture(
          `**${entier(proposees)} règle(s) proposée(s)** attendent une décision : elles ne dispensent rien tant qu’elles ne sont pas validées.`,
        ),
      );
    }
    zoneRegles.appendChild(
      regles.length === 0
        ? noteLecture('Aucune règle de dispense.')
        : table(
            'Règles de dispense',
            ['Règle', 'Motif', ['Lignes captées', 'num'], 'Statut', 'Actions'],
            regles.map((r) =>
              h(
                'tr',
                {},
                h('td', {}, critere(r)),
                h('td', {}, r.motif),
                h('td', { classe: 'num' }, entier(r.lignes)),
                h(
                  'td',
                  {},
                  STATUTS[r.statut],
                  // L'auteur (une adresse) n'est montré qu'à l'équipe ; la lecture voit la date.
                  h('div', { classe: 'note-lecture' }, admin ? `${r.modifiePar}, ${dateFr(r.modifieLe.slice(0, 10))}` : `le ${dateFr(r.modifieLe.slice(0, 10))}`),
                ),
                h('td', {}, actionsRegle(r)),
              ),
            ),
          ),
    );
    if (carteFormulaire) zoneRegles.appendChild(carteFormulaire);
  };

  const toutCharger = async (): Promise<void> => {
    try {
      const [d, { regles }] = await Promise.all([etatJustificatifs(exercice), reglesDispense()]);
      if (exercice === undefined) {
        exercice = d.exercice;
        exerciceCourant = d.exercice;
        for (const x of [d.exercice, d.exercice - 1]) choixExercice.appendChild(h('option', { value: String(x) }, String(x)));
        choixExercice.value = String(d.exercice);
      }
      dessinerEtat(d);
      dessinerRegles(regles);
      await chargerLignes();
    } catch (e) {
      vider(zoneChiffres);
      zoneChiffres.appendChild(noteLecture('L’état des justificatifs n’a pas pu être chargé.'));
      echec(e);
    }
  };
  choixExercice.addEventListener('change', () => {
    exercice = Number(choixExercice.value);
    filtreMois.value = '';
    void toutCharger();
  });

  void toutCharger();

  const panneauExercice = h(
    'div',
    { id: 'justificatifs-panneau-exercice', role: 'tabpanel', 'aria-labelledby': 'justificatifs-onglet-exercice' },
    h(
      'section',
      {},
      enteteSection(1, 'Justificatifs', 'Pièces des opérations du compte courant, d’après Zoho Books'),
      h('div', { classe: 'barre-actions' }, h('label', {}, 'Exercice ', choixExercice)),
      retour,
      zoneChiffres,
    ),
    h('section', {}, enteteSection(2, 'À qui demander', 'Les pièces des paiements par carte, par titulaire'), zoneCartes),
    h('section', {}, enteteSection(3, 'Semaine après semaine', 'Le dernier point de chaque semaine'), zoneSuivi),
    h('section', {}, enteteSection(4, 'Mois par mois', 'Justificatifs et rapprochement bancaire'), zoneMois),
    h(
      'section',
      {},
      enteteSection(5, 'Lignes', 'Par défaut, celles qui attendent une pièce'),
      h(
        'div',
        { classe: 'barre-actions' },
        h('label', {}, 'État ', filtreEtat),
        h('label', {}, 'Sens ', filtreSens),
        h('label', {}, 'Mois ', filtreMois),
        h('label', {}, 'Carte ', filtreCarte),
      ),
      zoneLignes,
    ),
    h(
      'section',
      {},
      enteteSection(6, 'Règles de dispense', 'Ce qui peut rester sans pièce, décidé ensemble'),
      noteLecture(
        'Une règle validée dispense les lignes sans pièce qu’elle capte et affiche son motif sur chacune. ' +
          'Une règle n’est jamais supprimée : elle est désactivée, et chaque changement est journalisé.',
      ),
      zoneRegles,
    ),
  );

  // L'onglet « Pièces reçues » n'existe que pour l'équipe : les pièces portent des noms et des montants.
  if (!admin) return h('div', { classe: 'page', id: 'page-justificatifs', role: 'region', 'aria-label': 'Justificatifs' }, panneauExercice);

  const pieces = ongletPiecesRecues();
  const panneauPieces = h(
    'div',
    { id: 'justificatifs-panneau-pieces', role: 'tabpanel', 'aria-labelledby': 'justificatifs-onglet-pieces', hidden: true },
    pieces.el,
  );
  let piecesChargees = false;
  const onglet = (cle: 'exercice' | 'pieces', libelle: string, actif: boolean): HTMLButtonElement => {
    const b: HTMLButtonElement = h(
      'button',
      {
        classe: 'tab',
        type: 'button',
        role: 'tab',
        id: `justificatifs-onglet-${cle}`,
        'aria-controls': `justificatifs-panneau-${cle}`,
        'aria-selected': actif ? 'true' : 'false',
        surClick: () => {
          const surPieces = cle === 'pieces';
          for (const x of barre.querySelectorAll('[role="tab"]')) x.setAttribute('aria-selected', x === b ? 'true' : 'false');
          panneauExercice.hidden = surPieces;
          panneauPieces.hidden = !surPieces;
          if (surPieces && !piecesChargees) {
            piecesChargees = true;
            pieces.charger();
          }
        },
      },
      libelle,
    );
    return b;
  };
  const barre = h(
    'div',
    { classe: 'tabs', role: 'tablist', 'aria-label': 'Justificatifs' },
    onglet('exercice', 'Exercice', true),
    onglet('pieces', 'Pièces reçues', false),
  );

  return h('div', { classe: 'page', id: 'page-justificatifs', role: 'region', 'aria-label': 'Justificatifs' }, barre, panneauExercice, panneauPieces);
}
